/* eslint-disable import/no-unresolved */
import { useEffect, useState } from 'react';
import { getIncomers } from 'react-flow-renderer';

import { getNodeMaxId } from 'utils';
import ConfigurationTab from '../components/ConfigurationTab';
import AppendContainer from './reconciliationtype/AppendContainer';
import JoinContainer from './reconciliationtype/JoinContainer';
import AggregateReconContainer from './reconciliationtype/AggregateReconContainer';

const getEdges = elements => elements.filter(e => (e.source && e.target));

const getNodes = elements => elements.filter(e => (!e.source && !e.target));

const getSrcNodes = (elements, selectedNode) => {
    const edges = getEdges(elements);
    return edges.filter(e => (e.target === selectedNode.id)).map(e => (e.source));
};

const getSrcInputs = (elements, selectedNode) => {
    const nodes = getNodes(elements);
    const srcNodes = getSrcNodes(elements, selectedNode);
    return nodes.filter(n => srcNodes.includes(n.id));
};

/* function to get existing configuration based on node id */
const getConfiguration = (node, saveConfig, subProjects) => {
    let config = saveConfig[node.id];
    /* if no config found check if that node is sub project and fetch sub project
    * config
    */
    if (!config || Object.keys(saveConfig).length === 0) {
        const issubProject = subProjects.filter(sp => node.data.label === sp.projectName);
        if (issubProject && issubProject.length > 0) {
            const subProjectConfig = issubProject[0].diagramFlow;
            const key = getNodeMaxId(subProjectConfig.elements);
            config = issubProject[0].flowConfig[`dndnode_${key}`];
        } else {
            config = null;
        }
    }
    return config;
};

const getSourceNodeDetails = (srcNodes, saveConfig, subProjects, projectId) => srcNodes.map(sc => {
    let key = null;
    let subProjectId = null;
    const subProjectConfig = sc.type === 'special' ? subProjects.filter(sp => sc.data.label === sp.projectName)[0] : null;
    if (sc.type !== 'input') {
        const defaultSrcNodes = getConfiguration(sc, saveConfig, subProjects);
        const parentProjectId = sc.type === 'special' ? sc.data.projectId : projectId;
        /** if node type is special(sub project) set key as max node
         * id from sub project configuration or set key value from
         * selected node id.
        */
        key = sc.type === 'special' ? getNodeMaxId(subProjectConfig.diagramFlow.elements) : sc.id.split('_')[1];
        /* get subProjectId from the previously fetched configuration
        */
        if (defaultSrcNodes && !defaultSrcNodes.isSourceSubProject && !defaultSrcNodes.isTargetSubProject) {
            subProjectId = parentProjectId;
        } else if (defaultSrcNodes && !defaultSrcNodes.isSourceSubProject && defaultSrcNodes.isTargetSubProject) {
            subProjectId = defaultSrcNodes.targetSubProjectId;
        } else {
            subProjectId = defaultSrcNodes.sourceSubProjectId;
        }

        /* return source nodes object, fileName, selectedColumns, fileDisplayName
        */
        return {
            ...sc,
            fileName: (subProjectId && parentProjectId)
                ? `${parentProjectId}_${subProjectId}_dndnode_${key}_result.csv`
                : `${parentProjectId}_${parentProjectId}_${sc.id}_result.csv`,
            selectedColumns: defaultSrcNodes.resultSelectedColumns,
            fileDisplayName: `${sc.data.label}_result.csv`,
        };
    }
    return sc;
});

const getUpdatedConfigData = (oldConfigData, latestConfigData) => oldConfigData.updatedConfigData.map(item => {
    const updatedNodeData = latestConfigData.find(({ id }) => id === item.id);
    if (updatedNodeData) {
        let newSelectedColumns = item.selectedColumns.map(col => {
            const updatedCol = updatedNodeData.selectedColumns.find(({ column }) => col.column === column);
            if (updatedCol) {
                return {
                    ...col,
                    operation: updatedCol.operation,
                    filters: updatedCol.filters,
                    transformation: updatedCol.transformation,
                    end_date: updatedCol.end_date || col.end_date,
                    start_date: updatedCol.start_date || col.start_date,
                };
            }
            return null;
        });
        newSelectedColumns = newSelectedColumns.filter(nc => nc !== null);
        const differenceColumn = updatedNodeData.selectedColumns
            .filter(
                ({ column: column1 }) => !item.selectedColumns
                    .some(
                        ({ column: column2 }) => column2 === column1,
                    ),
            );
        if (differenceColumn.length > 0) {
            newSelectedColumns = newSelectedColumns.concat(differenceColumn);
        }
        return {
            ...item,
            selectedColumns: newSelectedColumns,
        };
    }
    const differenceArray = latestConfigData.filter(({ id: id1 }) => !oldConfigData.updatedConfigData.some(({ id: id2 }) => id2 === id1));
    return differenceArray.length > 0 ? differenceArray[0] : null;
});

const getDisplayName = selectedColumn => {
    const isColumnAvailable = selectedColumn?.column;
    const displayNameContainsDndNode = isColumnAvailable && selectedColumn.column.includes('dndnode_');
    const isDisplayNameAvailable = selectedColumn?.displayName;
    if (displayNameContainsDndNode) {
        return `${selectedColumn.column.split('dndnode_')[0]}${selectedColumn.fileName}`;
    }
    return isDisplayNameAvailable ? selectedColumn.displayName : selectedColumn.column;
};

const getFileDataWhenConfigIsNotAvailable = data => data && data.map(c => ({
    ...c,
    selectedColumns: c.selectedColumns.map(sc => ({
        ...sc,
        isSourceColumn: false,
        isTargetColumn: false,
        isSourceGroupBy: false,
        isTargetGroupBy: false,
        displayName: getDisplayName(sc),
    })),
}));

/* function to get source nodes of the selected recon function node or if node type is default */
const getSourceNodes = (selectedType, selectedNode, elements, saveConfig, subProjects, projectId) => {
    if (selectedType === 'default') {
        const noOfIncomers = 2;
        if (getIncomers(selectedNode, elements).length < noOfIncomers) {
            return { isError: true, errorMsg: 'Invalid config! Each Recon Function must have 2 inputs.', data: null };
        }
        let srcInputs = getSrcInputs(elements, selectedNode);
        // get source node details
        srcInputs = getSourceNodeDetails(srcInputs, saveConfig, subProjects, projectId);
        // if source node is of input type, return srcInputs.
        return { isError: false, errorMsg: null, data: srcInputs };
    }
    // if no source node found return empty data
    return { isError: false, errorMsg: null, data: [] };
};

/* function to get file data from the source nodes */
const getFileData = (scNodes, columnsData, selectedNode, saveConfig, subProjects) => {
    const getSelectedCols = node => (
        node.type === 'input'
            ? columnsData.filter(c => c.id === node.id)[0].columns.filter(item => item.isChecked)
            : node.selectedColumns
    );
    const updatedFileData = scNodes && scNodes.map(sc => {
        // if source node type is input return selected columns from columns data, otherwise return selected columns from source node object
        const selCols = getSelectedCols(sc);
        return {
            ...sc,
            id: sc.id,
            fileId: sc?.data?.fileId,
            fileName: sc.data.fileName || sc.fileName,
            selectedColumns: selCols,
            fileDisplayName: sc.type !== 'default' ? sc.data.label : `${sc.data.label}_result.csv`,
            isSubProject: sc.type === 'special',
        };
    });
    /* if source nodes array length is > 0 and already config of the recon type selected is available */
    if (scNodes?.length > 0 && saveConfig && Object.keys(saveConfig).length) {
        // fetch old config
        const oldConfig = getConfiguration(selectedNode, saveConfig, subProjects);
        if (oldConfig) {
            // if old config is present, update the selected columns according to latest file data
            return getUpdatedConfigData(oldConfig, updatedFileData);
        }
    }
    return updatedFileData;
};

const ConfigurationTabContainer = ({
    selectedNode, columnsData, showColumnsModal, setShowColumnsModal,
    onSaveData, elements, saveConfig, onFilterChange, onOperationChange,
    subProjects, projectId, error, setError,
}) => {
    const { id: nodeId, type: selectedType, data: { label = '', fileName = '', fileId } = {} } = selectedNode;
    const [sourceNodes, setSourceNodes] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState({});
    const [fileData, setFileData] = useState([]);

    /* function to save the latest configuration to state variable */
    const saveData = configData => {
        const configId = selectedNode.id;
        const updatedValue = {
            ...configData.flow,
        };
        onSaveData({
            [configId]: updatedValue,
        });
    };

    /* callback function when Show Columns button is clicked */
    const handleClick = () => {
        setShowColumnsModal(!showColumnsModal);
    };

    const getComponentWhenOldConfigIsAvailable = oldConfig => {
        // if oldConfig is found update the fileData accordingly with the latest selected node configuration, because fileData is not updated before component is rendered
        if (oldConfig) {
            let fData;
            let resultArray = oldConfig.updatedConfigData;
            const { isError, data } = getSourceNodes(selectedType, selectedNode, elements, saveConfig, subProjects, projectId);
            if (!isError && data) {
                fData = getFileData(data, columnsData, selectedNode, saveConfig, subProjects);
                resultArray = fData && fData.map(c => ({
                    ...c,
                    selectedColumns: c.selectedColumns.map(sc => ({
                        ...sc,
                        isSourceColumn: sc?.isSourceColumn || false,
                        isTargetColumn: sc?.isTargetColumn || false,
                        isSourceGroupBy: sc?.isSourceGroupBy || false,
                        isTargetGroupBy: sc?.isTargetGroupBy || false,
                        displayName: getDisplayName(sc),
                    })),
                }));
                return resultArray;
            }
            return resultArray;
        }
        return [];
    };

    const getAppendComponent = () => {
        let updatedConfigData = fileData;
        // update file data selected columns with isConcateCheck and displayName when no configuration is found
        if ((!saveConfig && Object.keys(saveConfig).length === 0)) {
            updatedConfigData = fileData && fileData.map(c => ({
                ...c,
                selectedColumns: c.selectedColumns.map(sc => ({
                    ...sc,
                    isConcateCheck: false,
                    displayName: sc.column.includes('dndnode_') ? `${sc.column.split('dndnode_')[0]}${sc.fileName}` : sc.column,
                })),
            }));
        }
        return (
            <AppendContainer
                configData={updatedConfigData}
                saveData={saveData}
                setError={setError}
            />
        );
    };
    const getJoinComponent = () => {
        let updatedConfigData = fileData;
        let subOperation = 'inner';
        // update file data selected columns with isJoin when no configuration is found
        if ((!saveConfig && Object.keys(saveConfig).length === 0)) {
            updatedConfigData = fileData && fileData.map(c => ({
                ...c,
                selectedColumns: c.selectedColumns.map(sc => ({
                    ...sc,
                    isJoin: false,
                })),
            }));
        } else {
            const oldConfig = getConfiguration(selectedNode, saveConfig, subProjects);
            if (oldConfig) {
                // update subOperation if oldConfig is found
                subOperation = oldConfig.subOperation;
            }
        }
        return (
            <JoinContainer
                configData={updatedConfigData}
                saveData={saveData}
                subOperation={subOperation}
                setError={setError}
            />
        );
    };

    const getAggregateReconComponent = () => {
        let updatedConfigData;
        let oldConfig = null;
        if ((!saveConfig && Object.keys(saveConfig).length === 0) || saveConfig[nodeId] === undefined) {
            // update file data selected columns with isSourceColumn, isTargetColumn, isSourceGroupBy, isTargetGroupBy and displayName when no configuration is found
            updatedConfigData = getFileDataWhenConfigIsNotAvailable(fileData);
        } else {
            oldConfig = getConfiguration(selectedNode, saveConfig, subProjects);
            updatedConfigData = getComponentWhenOldConfigIsAvailable(oldConfig);
        }
        return (
            <AggregateReconContainer
                configData={updatedConfigData}
                saveData={saveData}
                oldConfig={oldConfig}
                setError={setError}
                operation={label}
            />
        );
    };

    /* side effect when selected node id is changed */
    useEffect(() => {
        if (selectedType === 'default') {
            const { isError, errorMsg, data } = getSourceNodes(selectedType, selectedNode, elements, saveConfig, subProjects, projectId);
            if (isError) {
                setError({ message: errorMsg });
            }
            if (!isError && data) {
                setSourceNodes(data);
            }
        }
    }, [selectedNode.id]);

    /* side effect when selectedType and columnData is changed */
    useEffect(() => {
        if (Object.keys(selectedNode).length > 0 && selectedType === 'input') {
            const { columns: cols } = columnsData.filter(item => item.id === nodeId)[0];
            const configData = cols.filter(item => item.isChecked);
            setSelectedColumns({ fileId, fileName, selectedColumns: configData });
        }
    }, [selectedType, columnsData]);

    /* side effect when sourceNodes is changed */
    useEffect(() => {
        if (sourceNodes.length > 0) {
            const newFileData = getFileData(sourceNodes, columnsData, selectedNode, saveConfig, subProjects);
            setFileData(newFileData);
        }
    }, [sourceNodes]);

    return (
        <ConfigurationTab
            selectedNode={selectedNode}
            label={label}
            onFilterChange={onFilterChange}
            onOperationChange={onOperationChange}
            error={error}
            onSaveData={onSaveData}
            selectedColumns={selectedColumns}
            handleClick={handleClick}
            getAppendComponent={getAppendComponent}
            selectedType={selectedType}
            getJoinComponent={getJoinComponent}
            getAggregateReconComponent={getAggregateReconComponent}
        />
    );
};

export default ConfigurationTabContainer;
